import React, { useState, useEffect, useRef } from "react";
import Dropdown from "../../Components/CustomDropdown/customdropdown";
import { toast } from "react-toastify";
import { 
  get_user_accounts, 
  get_account_detail
} from '../../services/common';

const Reports = () => {
  const BASE_URL = process.env.REACT_APP_EXISTING_BASE_URL;
  const systemToken = localStorage.getItem('systemToken') || "";
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("transaction");
  
  // Separate loading states for each button
  const [isGeneratingTransaction, setIsGeneratingTransaction] = useState(false);
  const [isGeneratingPortfolio, setIsGeneratingPortfolio] = useState(false);

  // Transaction Report States
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedSubAccount, setSelectedSubAccount] = useState("");
  const [reportType, setReportType] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userAccounts, setUserAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);

  const [preSelectedAccountType, setPreSelectedAccountType] = useState({});
  const [preSelectedType, setPreSelectedType] = useState({});
  const [myinvestment, setMyinvestment]= useState();
  const [accountSlug, setAccountSlug] = useState("");

  // Portfolio Report States
  const [portfolioConfirm, setPortfolioConfirm] = useState(false);

  // Reports List States
  const [reportsList, setReportsList] = useState([]);

  // Form state for API calls
  const [form, setForm] = useState({
    userac_id: '',
    subac_id: '',
    ttype: 'all',
    from_date: '',
    to_date: '',
  });

  // Error and success states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Account data for dropdowns
  const [accountData, setAccountData] = useState([]);
  const [subAccountData, setSubAccountData] = useState([]);
  const [typeData, setTypeData] = useState([
    { value: "all", label: "All" },
    { value: "custom", label: "Custom" }
  ]);

  const headers = [
    { label: "Document ID", key: "diD" },
    { label: "Status", key: "status" },
    { label: "Document Name", key: "name" },
    { label: "Date Added", key: "date" },
    { label: "", key: "" },
    { label: "", key: "" },
  ];

  const handleSort = (key) => {};

  // Fetch user accounts on component mount
  const fetchUserAccounts = async () => {
    try {
      setIsLoading(true);
      const data = await get_user_accounts(systemToken);
      
      if (data.success === 200) {
        const accounts = data.accounts || [];
        const formattedAccounts = accounts.map(account => ({
          value: account.userac_id.toString(),
          label: account.ac_name,
          ac_slug: account.ac_slug
        }));
        setUserAccounts(accounts);
        setAccountData(formattedAccounts);
      } else {
        toast.error(data.message || 'Failed to fetch accounts');
      }
    } catch (error) {
      console.error('Error fetching user accounts:', error);
      toast.error('Failed to fetch user accounts');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch sub accounts when main account is selected
  const fetchSubAccounts = async (accountId, accountSlug) => {
    try {
      if (!accountId || accountId === '0') {
        setSubAccountData([]);
        setSubAccounts([]);
        return;
      }

      const data = await get_account_detail(systemToken, accountSlug);
      
      if (data.success === 200) {
        const subAccounts = data.ac_data?.subaccounts || [];
        const formattedSubAccounts = subAccounts.map(subAccount => ({
          value: subAccount.subac_id.toString(),
          label: `${subAccount.subac_name} (${subAccount.currency_name})`
        }));
        setSubAccounts(subAccounts);
        setSubAccountData(formattedSubAccounts);
      } else {
        toast.error(data.message || 'Failed to fetch sub accounts');
      }
    } catch (error) {
      console.error('Error fetching sub accounts:', error);
      toast.error('Failed to fetch sub accounts');
    }
  };

  const handleSelectAccountType = (selectedOption) => {
    const accountId = selectedOption.value;
    const account = userAccounts.find(acc => acc.userac_id.toString() === accountId);
    
    setSelectedAccount(accountId);
    setForm(prev => ({ ...prev, userac_id: accountId }));
    
    if (account) {
      setAccountSlug(account.ac_slug);
      fetchSubAccounts(accountId, account.ac_slug);
    }
  };

  const handleSelectSubAccount = (selectedOption) => {
    const subAccountId = selectedOption.value;
    setSelectedSubAccount(subAccountId);
    setForm(prev => ({ ...prev, subac_id: subAccountId }));
  };

  const handleSelectType = (selectedOption) => {
    const type = selectedOption.value;
    setReportType(type);
    setForm(prev => ({ ...prev, ttype: type }));
  };

  const handleDateChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'from_date') {
      setFromDate(value);
    } else if (field === 'to_date') {
      setToDate(value);
    }
  };

  const fetch_account_detail = async (ac_name) => {
    try {
      const data = await get_account_detail(systemToken, ac_name || accountSlug);
      
      if (data.success === 200) {
        const myinvestmentData = data.ac_info;
        if (myinvestmentData.userac_establish_date) {
          myinvestmentData.userac_establish_date = new Date(myinvestmentData.userac_establish_date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          });
        }
        setMyinvestment(myinvestmentData);
        setSubAccounts(data.ac_data.subaccounts);
      }
    } catch (error) {
      console.error('Error fetching account detail:', error);
    }
  };

  // API Functions
  const generateTransactionReport = async () => {
    if (!form.userac_id || form.userac_id === '0') {
      toast.warning("Please select a valid account for transaction report");
      return;
    }
    if (!form.subac_id || form.subac_id === '0') {
      toast.warning("Please select a valid sub account for transaction report");
      return;
    }

    setIsGeneratingTransaction(true);
    setError('');
    setSuccess('');
    
    try {
      const formData = new FormData();
      formData.append('device', 'vue');
      formData.append('userac_id', form.userac_id);
      formData.append('subac_id', form.subac_id);
      formData.append('ttype', form.ttype);
      if (form.ttype === 'custom') {
        formData.append('from_date', form.from_date);
        formData.append('to_date', form.to_date);
      }
      formData.append('user_token', localStorage.getItem('systemToken') || 'demo-token');

      const response = await fetch(`${BASE_URL}/trans-report-request`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (data.success === 200) {
        // setSuccess(data.message);
        toast.success(data.message);
        // Refresh reports list
        fetchReports();
      } else {
        setError(data.message);
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error generating transaction report:', error);
      setError('Failed to generate transaction report');
      toast.error('Failed to generate transaction report');
    } finally {
      setIsGeneratingTransaction(false);
    }
  };

  const generatePortfolioReport = async () => {
    setIsGeneratingPortfolio(true);
    setError('');
    setSuccess('');
    
    try {
      const formData = new FormData();
      formData.append('device', 'vue');
      formData.append('user_token', localStorage.getItem('systemToken') || 'demo-token');

      const response = await fetch(`${BASE_URL}/portfolio-report-request`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (data.success === 200) {
        // setSuccess(data.message);
        toast.success(data.message);
        // Refresh reports list
        fetchReports();
      } else {
        setError(data.message);
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error generating portfolio report:', error);
      setError('Failed to generate portfolio report');
      toast.error('Failed to generate portfolio report');
    } finally {
      setIsGeneratingPortfolio(false);
    }
  };

  const fetchReports = async () => {
    setIsLoading(true);
    try {
      // Mock data for now - replace with actual API call
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserAccounts();
    fetchReports();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600 mt-1">Generate transaction and portfolio reports</p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Transaction Report Card */}
          <div className="xl:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <img
                      src="/note-black.svg"
                      alt="Transaction Report"
                      className="h-6 w-6 text-white"
                    />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Transaction Report</h2>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6">
                {/* Error and Success Messages */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}
                {success && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-green-600 text-sm">{success}</p>
                  </div>
                )}

                {/* Form Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Dropdowns */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Account
                      </label>
                      <Dropdown
                        label=""
                        options={accountData}
                        preselectedValue={preSelectedAccountType}
                        onSelect={handleSelectAccountType}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Sub Account
                      </label>
                      <Dropdown
                        label=""
                        options={subAccountData}
                        preselectedValue={preSelectedAccountType}
                        onSelect={handleSelectSubAccount}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Report Type
                      </label>
                      <Dropdown
                        label=""
                        options={typeData}
                        preselectedValue={preSelectedType}
                        onSelect={handleSelectType}
                      />
                    </div>
                  </div>

                  {/* Right Column - Date Inputs and Button */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Date
                      </label>
                      <input
                        type="date"
                        value={form.from_date}
                        onChange={(e) => handleDateChange('from_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To Date
                      </label>
                      <input
                        type="date"
                        value={form.to_date}
                        onChange={(e) => handleDateChange('to_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div className="pt-4">
                      <button
                        type="button"
                        onClick={generateTransactionReport}
                        disabled={isGeneratingTransaction || isGeneratingPortfolio}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-md transition duration-200 flex items-center justify-center space-x-2"
                      >
                        {isGeneratingTransaction ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>Generate Report</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Report Card */}
          <div className="xl:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-fit">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <img
                      src="/user-octagon.svg"
                      alt="Portfolio Report"
                      className="h-6 w-6 text-white"
                    />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Portfolio Report</h2>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6">
                <div className="text-center">
                  <div className="mb-4">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3">
                      <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Generate Portfolio Report</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Generate a comprehensive portfolio report with all your investment details and performance metrics.
                    </p>
                  </div>
                  
                  <button
                    type="button"
                    onClick={generatePortfolioReport}
                    disabled={isGeneratingTransaction || isGeneratingPortfolio}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-md transition duration-200 flex items-center justify-center space-x-2"
                  >
                    {isGeneratingPortfolio ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Generate Now</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        
      </div>
    </div>
  );
};

export default Reports;
